/**
 * Website-analytics consent — the pure half (no DOM, no globals, unit-tested).
 *
 * The landing site (4klabs.ai), the API and this app share one contract (v1):
 * a visitor's consent choice lives in the `4k_consent` cookie on Domain=.4klabs.ai,
 * so a choice made on the marketing site is honoured here silently, and vice versa.
 * The DOM side (reading cookies, loading GA4/Clarity, the bar) is consentGate.ts.
 *
 * States, computed client-side and sent with every collect payload:
 *   gpc     — navigator.globalPrivacyControl === true: no GA4/Clarity, ever.
 *   granted — 4k_consent=granted: GA4 + Clarity.
 *   denied  — 4k_consent=denied: cookieless counting only.
 *   pending — opt-in region (EU/EEA/UK/CH by IANA timezone) and no choice: bar shown, nothing loaded.
 *   notice  — every other region, no choice: GA4 + Clarity on, one-time "Opt out" notice. GA4 still
 *             defaults to denied by IP in the opt-in countries, for a device whose timezone says elsewhere.
 * "Allowed" = granted | notice. When not allowed, analytics cookies from an earlier visit are removed.
 */

export type ConsentState = 'granted' | 'notice' | 'denied' | 'pending' | 'gpc';
export type ConsentChoice = 'granted' | 'denied';
export type ConsentUi = 'bar' | 'notice' | 'none';

export const CONSENT_COOKIE = '4k_consent';
export const NOTICE_COOKIE = '4k_notice';
export const VISITOR_COOKIE = '4k_vid';
export const FIRST_TOUCH_COOKIE = '4k_ft';
export const LAST_TOUCH_COOKIE = '4k_lt';
export const SESSION_COOKIE = '4k_sid';
/** 395 days, the contract's max-age for 4k_consent / 4k_notice. */
export const CONSENT_MAX_AGE_S = 395 * 24 * 60 * 60;

/** Back-compat with the landing's old consent script (and useVideoAnalytics). */
export const LEGACY_CONSENT_KEY = 'analytics_consent';
export const CONSENT_CHANGE_EVENT = 'analytics_consent_change';

export const PRIVACY_POLICY_URL = 'https://4klabs.ai/privacy-policy';

// ---------------------------------------------------------------------------
// Region
// ---------------------------------------------------------------------------

const OPT_IN_ZONES = new Set([
  'Atlantic/Canary',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Atlantic/Faroe',
  'Atlantic/Reykjavik',
  'Arctic/Longyearbyen',
  'Asia/Nicosia',
  'Asia/Famagusta',
  'America/Guadeloupe',
  'America/Martinique',
  'America/Cayenne',
  'America/Marigot',
  'America/St_Barthelemy',
  'Indian/Reunion',
  'Indian/Mayotte',
]);

/** EEA + UK + CH + the EU outermost regions with their own ISO codes, for GA's region-specific default. */
export const OPT_IN_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH', 'GP', 'MQ', 'GF', 'RE', 'YT', 'MF', 'BL',
];

/**
 * True when the visitor must opt IN: an `Europe/*` zone, the listed Atlantic/Arctic,
 * Cyprus and EU outermost-region zones, or no timezone at all (fail closed).
 */
export function isOptInRegion(tz: string | null | undefined): boolean {
  if (!tz) return true;
  return tz.startsWith('Europe/') || OPT_IN_ZONES.has(tz);
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

export function parseChoice(value: string | null | undefined): ConsentChoice | null {
  return value === 'granted' || value === 'denied' ? value : null;
}

export function computeConsentState(input: {
  gpc: boolean;
  choice: ConsentChoice | null;
  tz: string | null | undefined;
}): ConsentState {
  if (input.gpc) return 'gpc';
  if (input.choice) return input.choice;
  return isOptInRegion(input.tz) ? 'pending' : 'notice';
}

export function isAllowed(state: ConsentState): boolean {
  return state === 'granted' || state === 'notice';
}

/** The state after the visitor clicks Accept / Decline / Opt out. GPC outranks a click. */
export function applyChoice(state: ConsentState, choice: ConsentChoice): ConsentState {
  return state === 'gpc' ? 'gpc' : choice;
}

/**
 * Which UI to show. Never inside an iframe and never on a share/player/learn/embed
 * route (it would cover the video) — those apply the stored choice / region default
 * silently.
 */
export function consentUi(
  state: ConsentState,
  ctx: { embedded: boolean; quietRoute: boolean; noticeDismissed: boolean },
): ConsentUi {
  if (ctx.embedded || ctx.quietRoute) return 'none';
  if (state === 'pending') return 'bar';
  if (state === 'notice' && !ctx.noticeDismissed) return 'notice';
  return 'none';
}

const QUIET_ROUTE = /^\/(video|embed|share|learn|blog)(\/|$)|^\/start\/?$/;

/**
 * Share / player / learn / embed / blog pages, and the front door's storyboard (/start), where the sign-in sheet
 * sits at the bottom of a phone: no bar, no notice. /landing, before it, still asks.
 */
export function isQuietRoute(pathname: string): boolean {
  return QUIET_ROUTE.test(pathname);
}

/** Google Consent Mode v2 `default` for a state (ads signals are always denied). */
export function consentModeDefault(state: ConsentState) {
  return {
    analytics_storage: isAllowed(state) ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  } as const;
}

/** The notice state's second `default`: GA4 storage denied by IP in the opt-in countries. */
export function consentModeRegionDefault(state: ConsentState) {
  if (state !== 'notice') return null;
  return {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    region: OPT_IN_COUNTRIES,
  } as const;
}

// ---------------------------------------------------------------------------
// Cookies
// ---------------------------------------------------------------------------

/** Raw cookie values by name (NOT URI-decoded — touch cookies decode in decodeTouch). First wins. */
export function parseCookies(cookieString: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!cookieString) return out;
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const name = part.slice(0, eq).trim();
    if (!name || name in out) continue;
    out[name] = part.slice(eq + 1).trim();
  }
  return out;
}

/** `.4klabs.ai` on 4klabs.ai and its subdomains; null (host-only) on localhost / preview hosts. */
export function cookieDomainFor(hostname: string): string | null {
  const h = hostname.toLowerCase();
  return h === '4klabs.ai' || h.endsWith('.4klabs.ai') ? '.4klabs.ai' : null;
}

export function serializeCookie(
  name: string,
  value: string,
  opts: { maxAgeS: number; domain: string | null; secure: boolean },
): string {
  let s = `${name}=${value}; Path=/; Max-Age=${opts.maxAgeS}; SameSite=Lax`;
  if (opts.domain) s += `; Domain=${opts.domain}`;
  if (opts.secure) s += '; Secure';
  return s;
}

/**
 * Set-cookie strings that delete the analytics cookies present in `cookieNames`:
 * 4k_vid/ft/lt/sid, GA's _ga / _ga_<id>, Clarity's _clck/_clsk. Each is expired both
 * on the shared domain and host-only, since GA/Clarity may have written either.
 */
export function analyticsCookieDeletions(cookieNames: string[], hostname: string): string[] {
  const own = new Set([VISITOR_COOKIE, FIRST_TOUCH_COOKIE, LAST_TOUCH_COOKIE, SESSION_COOKIE, '_clck', '_clsk']);
  const domain = cookieDomainFor(hostname);
  const out: string[] = [];
  for (const name of cookieNames) {
    if (!own.has(name) && !/^_ga($|_)/.test(name)) continue;
    out.push(serializeCookie(name, '', { maxAgeS: 0, domain: null, secure: false }));
    if (domain) out.push(serializeCookie(name, '', { maxAgeS: 0, domain, secure: false }));
  }
  return out;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string | null | undefined): value is string {
  return !!value && UUID_RE.test(value);
}

// ---------------------------------------------------------------------------
// Touch (first / last touch cookies)
// ---------------------------------------------------------------------------

export type Channel =
  | 'ai_assistant'
  | 'organic_search'
  | 'paid_search'
  | 'paid_social'
  | 'social'
  | 'email'
  | 'referral'
  | 'internal'
  | 'direct';

export interface Touch {
  ch: Channel;
  src: string;
  med: string;
  cmp?: string;
  trm?: string;
  cnt?: string;
  /** Referrer origin+path, no query. */
  ref?: string;
  /** Landing path, no query. */
  lp: string;
  /** Epoch ms. */
  ts: number;
  gclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  fbclid?: string;
}

/** `4k_ft` / `4k_lt` hold `encodeURIComponent(JSON.stringify(Touch))`. Returns null for anything malformed. */
export function decodeTouch(raw: string | null | undefined): Touch | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(decodeURIComponent(raw));
    if (
      v &&
      typeof v === 'object' &&
      typeof v.ch === 'string' &&
      typeof v.lp === 'string' &&
      typeof v.ts === 'number' &&
      Number.isFinite(v.ts)
    ) {
      return v as Touch;
    }
  } catch {
    /* malformed cookie */
  }
  return null;
}

export function encodeTouch(touch: Touch): string {
  return encodeURIComponent(JSON.stringify(touch));
}
